import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { CheckCircle, Minus, Plus } from "@phosphor-icons/react";
import ArtDecoDivider from "../components/ArtDecoDivider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
    { value: "cocktail_party", label: "Cocktail Party" },
    { value: "wedding", label: "Wedding" },
    { value: "casual_gathering", label: "Casual Gathering" },
    { value: "corporate", label: "Corporate" },
    { value: "birthday", label: "Birthday" },
    { value: "holiday_party", label: "Holiday Party" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Defined at module scope so React does not unmount/remount inputs on every parent re-render.
function Field({ label, required, error, children, testid }) {
    return (
        <div className="mb-6" data-testid={testid}>
            <label className="label-overline">
                {label}
                {required && <span className="text-gold ml-1">·</span>}
            </label>
            {children}
            {error && (
                <div className="text-xs text-red-400 mt-1 font-sans" data-testid={`${testid}-error`}>
                    {error}
                </div>
            )}
        </div>
    );
}

// Custom time selector retired — duration is sufficient. Component intentionally removed.

export default function Book() {
    const [form, setForm] = useState({
        full_name: "",
        email: "",
        phone: "",
        event_date: "",
        duration_hours: 4,
        location: "",
        event_type: "",
        guest_count: "",
        specialty_cocktails: "",
        notes: "",
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [serverError, setServerError] = useState("");

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = () => {
        const e = {};
        if (!form.full_name.trim()) e.full_name = "Required";
        if (!form.email.trim()) e.email = "Required";
        else if (!EMAIL_RE.test(form.email.trim())) e.email = "Invalid email";
        if (!form.phone.trim()) e.phone = "Required";
        if (!form.event_date) e.event_date = "Required";
        if (!form.duration_hours || form.duration_hours < 1) e.duration_hours = "Must be ≥ 1";
        if (!form.location.trim()) e.location = "Required";
        if (!form.event_type) e.event_type = "Pick an event type";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        setServerError("");
        if (!validate()) return;
        setSubmitting(true);
        try {
            const payload = {
                full_name: form.full_name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim(),
                event_date: form.event_date,
                duration_hours: Number(form.duration_hours),
                location: form.location.trim(),
                event_type: form.event_type,
                guest_count: form.guest_count ? Number(form.guest_count) : null,
                specialty_cocktails: form.specialty_cocktails.trim() || null,
                notes: form.notes.trim() || null,
            };
            const res = await axios.post(`${API}/bookings`, payload);
            setResult(res.data);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
            setServerError(typeof msg === "string" ? msg : "Something went wrong.");
        } finally {
            setSubmitting(false);
        }
    };

    if (result) {
        return (
            <div className="min-h-[80vh] flex items-center justify-center px-6 py-24" data-testid="booking-success">
                <div className="max-w-2xl w-full text-center">
                    <CheckCircle size={72} weight="thin" className="text-gold mx-auto mb-6" />
                    <div className="text-gold text-xs tracking-overline uppercase mb-3">
                        Reservation Received
                    </div>
                    <h1 className="font-display text-white text-4xl md:text-5xl mb-6">
                        Thank you, <em className="gold-italic">{result.full_name.split(" ")[0]}</em>
                    </h1>
                    <ArtDecoDivider />
                    <p className="text-white/70 text-base md:text-lg font-light leading-relaxed mb-8">
                        Your request has been delivered. We'll be in touch shortly to confirm
                        the details of your evening.
                    </p>
                    <div className="bg-obsidian-soft border border-white/10 p-6 mb-8 text-left">
                        <div className="text-xs tracking-overline uppercase text-white/40 mb-2">
                            Reference ID
                        </div>
                        <div className="font-mono text-gold text-sm break-all" data-testid="booking-reference-id">
                            {result.id}
                        </div>
                        {result.email_sent === false && (
                            <div className="mt-4 text-xs text-white/50 font-sans">
                                Note: confirmation email is queued — please keep this reference.
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/" className="btn-gold-outline" data-testid="booking-return-home">
                            Return Home
                        </Link>
                        <Link to="/calculator" className="btn-gold" data-testid="booking-go-calculator">
                            Try The Bar Calculator
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-obsidian min-h-screen" data-testid="page-book">
            {/* Header */}
            <section className="pt-16 pb-12 px-6 lg:px-12 text-center">
                <div className="text-gold text-xs tracking-overline uppercase mb-3">
                    Reservation Request
                </div>
                <h1 className="font-display text-white text-5xl md:text-6xl mb-4">
                    Pick Your <em className="gold-italic">Date</em>
                </h1>
                <ArtDecoDivider />
                <p className="text-white/60 max-w-xl mx-auto font-light">
                    Share a few details about your evening. We'll confirm by email within one business day.
                </p>
            </section>

            <form
                onSubmit={onSubmit}
                className="max-w-3xl mx-auto px-6 lg:px-12 pb-24"
                data-testid="booking-form"
                noValidate
            >
                <div className="bg-obsidian-soft border border-white/10 p-6 md:p-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                        <Field label="Full Name" required error={errors.full_name} testid="field-full-name">
                            <input
                                type="text"
                                className="input-dark"
                                value={form.full_name}
                                onChange={(e) => setField("full_name", e.target.value)}
                                data-testid="input-full-name"
                            />
                        </Field>
                        <Field label="Email" required error={errors.email} testid="field-email">
                            <input
                                type="email"
                                className="input-dark"
                                value={form.email}
                                onChange={(e) => setField("email", e.target.value)}
                                data-testid="input-email"
                            />
                        </Field>
                        <Field label="Phone" required error={errors.phone} testid="field-phone">
                            <input
                                type="tel"
                                className="input-dark"
                                value={form.phone}
                                onChange={(e) => setField("phone", e.target.value)}
                                data-testid="input-phone"
                            />
                        </Field>
                        <Field label="Location" required error={errors.location} testid="field-location">
                            <input
                                type="text"
                                className="input-dark"
                                placeholder="Venue, city, or address"
                                value={form.location}
                                onChange={(e) => setField("location", e.target.value)}
                                data-testid="input-location"
                            />
                        </Field>
                        <Field label="Event Date" required error={errors.event_date} testid="field-event-date">
                            <input
                                type="date"
                                className="input-dark"
                                value={form.event_date}
                                onChange={(e) => setField("event_date", e.target.value)}
                                data-testid="input-event-date"
                            />
                        </Field>
                        <Field label="Approx. Guest Count (optional)" error={null} testid="field-guests">
                            <input
                                type="number"
                                min={1}
                                className="input-dark"
                                value={form.guest_count}
                                onChange={(e) => setField("guest_count", e.target.value)}
                                data-testid="input-guests"
                            />
                        </Field>
                    </div>

                    {/* DURATION — promoted to its own prominent block */}
                    <div
                        className="mt-2 mb-8 bg-obsidian border border-gold/30 p-6 md:p-8"
                        data-testid="field-duration"
                    >
                        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
                            <div className="flex-1">
                                <div className="text-gold text-xs tracking-overline uppercase mb-2">
                                    Event Duration <span className="text-gold ml-1">·</span>
                                </div>
                                <h3 className="font-display text-white text-2xl md:text-3xl mb-1">
                                    How many <em className="gold-italic">hours</em>?
                                </h3>
                                <p className="text-white/50 text-sm font-light">
                                    Most cocktail parties run 3–5 hours. We'll arrive 60 minutes
                                    early to set the bar.
                                </p>
                            </div>
                            <div className="flex items-center gap-3 self-center sm:self-end">
                                <button
                                    type="button"
                                    onClick={() => setField("duration_hours", Math.max(1, Number(form.duration_hours) - 1))}
                                    className="w-14 h-14 border border-gold/40 text-gold hover:bg-gold hover:text-obsidian transition-colors flex items-center justify-center"
                                    data-testid="duration-minus"
                                    aria-label="Decrease duration"
                                >
                                    <Minus size={18} weight="thin" />
                                </button>
                                <div
                                    className="w-24 h-14 border border-gold/40 flex items-center justify-center font-display text-gold text-4xl"
                                    data-testid="input-duration-display"
                                >
                                    {form.duration_hours}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setField("duration_hours", Math.min(24, Number(form.duration_hours) + 1))}
                                    className="w-14 h-14 border border-gold/40 text-gold hover:bg-gold hover:text-obsidian transition-colors flex items-center justify-center"
                                    data-testid="duration-plus"
                                    aria-label="Increase duration"
                                >
                                    <Plus size={18} weight="thin" />
                                </button>
                            </div>
                        </div>
                        {errors.duration_hours && (
                            <div className="text-xs text-red-400 mt-3 font-sans" data-testid="field-duration-error">
                                {errors.duration_hours}
                            </div>
                        )}
                    </div>

                    <div className="mt-2">
                        <label className="label-overline">Event Type<span className="text-gold ml-1">·</span></label>
                        <div className="flex flex-wrap gap-2" data-testid="event-type-chips">
                            {EVENT_TYPES.map((et) => {
                                const active = form.event_type === et.value;
                                return (
                                    <button
                                        type="button"
                                        key={et.value}
                                        onClick={() => setField("event_type", et.value)}
                                        data-testid={`chip-${et.value}`}
                                        className={`px-4 py-2 text-xs tracking-overline uppercase border transition-all duration-200 ${
                                            active
                                                ? "bg-gold text-obsidian border-gold"
                                                : "border-white/15 text-white/70 hover:border-gold hover:text-gold"
                                        }`}
                                    >
                                        {et.label}
                                    </button>
                                );
                            })}
                        </div>
                        {errors.event_type && (
                            <div className="text-xs text-red-400 mt-2 font-sans">{errors.event_type}</div>
                        )}
                    </div>

                    <div className="mt-8">
                        <Field label="Specialty Cocktails (optional)" testid="field-specialty">
                            <textarea
                                rows={3}
                                className="input-dark resize-y"
                                placeholder="Any signature drinks you'd like featured? e.g. Espresso Martini, Paper Plane, Mom's Margarita..."
                                value={form.specialty_cocktails}
                                onChange={(e) => setField("specialty_cocktails", e.target.value)}
                                data-testid="input-specialty"
                            />
                        </Field>
                        <Field label="Notes (optional)" testid="field-notes">
                            <textarea
                                rows={3}
                                className="input-dark resize-y"
                                value={form.notes}
                                onChange={(e) => setField("notes", e.target.value)}
                                data-testid="input-notes"
                            />
                        </Field>
                    </div>

                    {serverError && (
                        <div className="mb-4 px-4 py-3 border border-red-500/30 bg-red-500/10 text-red-300 text-sm font-sans" data-testid="booking-server-error">
                            {serverError}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn-gold w-full mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="submit-booking"
                    >
                        {submitting ? "Sending Reservation…" : "Submit Reservation Request"}
                    </button>
                </div>
            </form>
        </div>
    );
}
